import React, { useState, useEffect } from "react";
import { IBusinessLinePackage } from "@/interfaces/Ibilling";
import {
  FiX,
  FiPackage,
  FiClock,
  FiInfo,
  FiTrendingUp,
  FiTrendingDown,
  FiCheckCircle,
  FiPhone,
  FiCalendar,
} from "react-icons/fi";
import { useGetAvailablePackages } from "@/api/availablepackages";

interface ChangePlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  currentPackage: IBusinessLinePackage;
  availablePackages: IBusinessLinePackage[];
  onConfirmChange: (
    newPackageId: string,
    effectiveFrom: "immediately" | "next_billing_cycle"
  ) => Promise<any>; // Changed to return any to handle the response
}

// Interface for the change plan response
interface ChangePlanResponse {
  business_line_package: IBusinessLinePackage;
  type: "upgrade" | "downgrade" | "payment_failed";
  charges?: Array<{
    id: string;
    status: string;
    created: string;
    payment_method: string;
    payment_intent: string;
    amount: number;
    currency: string;
    invoice: string | null;
    invoice_no: string | null;
  }>;
  invoice?: {
    id: string;
    status: string;
    number: string;
    period_start: string;
    period_end: string;
    total: number;
    currency: string;
    invoice_pdf: string;
  };
  error?: {
    message: string;
    status: number;
  };
}

const ChangePlanDialog: React.FC<ChangePlanDialogProps> = ({
  isOpen,
  onClose,
  currentPackage,
  // availablePackages,
  onConfirmChange,
}) => {
  console.log("current packages", currentPackage);
  const [selectedPackageIndex, setSelectedPackageIndex] = useState<number>(-1);
  const [effectiveFrom, setEffectiveFrom] = useState<
    "immediately" | "next_billing_cycle"
  >("next_billing_cycle");
  const [isLoading, setIsLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [changePlanResponse, setChangePlanResponse] =
    useState<ChangePlanResponse | null>(null);

  // Use the hook at the component level
  const {
    mutate: fetchAvailablePackages,
    data: availablePackagesResponse,
    isPending: isLoadingPackages,
  } = useGetAvailablePackages();

  // Determine phone type from current package
  const getPhoneType = (packageType: string): "regular" | "tollfree" => {
    // Use the package type field directly
    return packageType === "tollfree" ? "tollfree" : "regular";
  };

  const phoneType = getPhoneType(currentPackage.package?.type || "regular");

  // Use fetched packages or fall back to empty array
  const rawPackages = availablePackagesResponse?.data?.packages || [];

  // Transform the API response to match our expected structure
  const availablePackages = rawPackages.map((pkg: any) => ({
    id: pkg.id,
    package: {
      id: pkg.id,
      name: pkg.name,
      type: pkg.type,
      minutes: pkg.minutes,
      price: pkg.price,
      currency: pkg.currency,
      fee_per_overage_minute: pkg.fee_per_overage_minute,
      recurring: pkg.recurring,
    },
    status: "active",
    phone_number: currentPackage.phone_number,
    used_minutes: 0,
    pending_updated_package: null,
    add_ons: [],
    call_count: 0,
  }));

  // Debug log to see what we're getting from the API
  console.log("API Response:", availablePackagesResponse);
  console.log("Raw Packages:", rawPackages);
  console.log("Transformed Packages:", availablePackages);

  // Fetch available packages when component mounts
  useEffect(() => {
    if (isOpen && phoneType) {
      fetchAvailablePackages(phoneType);
    }
  }, [isOpen, phoneType, fetchAvailablePackages]);

  // Reset selection when packages change and set initial selection
  useEffect(() => {
    if (availablePackages.length > 0) {
      // Set initial selection to first available package
      setSelectedPackageIndex(0);
    } else {
      setSelectedPackageIndex(-1);
    }
  }, [availablePackages.length]);

  const handleConfirm = async () => {
    if (selectedPackageIndex < 0) return;

    const selectedPackage = availablePackages[selectedPackageIndex];
    if (!selectedPackage?.package) return;

    setIsLoading(true);
    try {
      const response = await onConfirmChange(selectedPackage.id, effectiveFrom);

      // Store the response and show summary
      if (response) {
        setChangePlanResponse(response);
        setShowSummary(true);
      }
    } catch (error: any) {
      console.error("Error changing plan:", error);

      // Handle 402 Payment Required error - show payment failure details
      if (error?.response?.status === 402) {
        const errorData = error.response.data;
        if (errorData?.detail) {
          // Create a mock response to show payment failure in summary
          const paymentFailureResponse = {
            business_line_package: currentPackage,
            type: "payment_failed" as any,
            charges: errorData.detail.charges || [],
            error: {
              message: errorData.detail.msg,
              status: 402,
            },
          };
          setChangePlanResponse(paymentFailureResponse);
          setShowSummary(true);
          return; // Don't close the dialog, show the error summary instead
        }
      }

      // For other errors, close the dialog
      onClose();
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setSelectedPackageIndex(-1);
    setEffectiveFrom("next_billing_cycle");
    setShowSummary(false);
    setChangePlanResponse(null);
    onClose();
  };

  const handleCloseSummary = () => {
    setShowSummary(false);
    setChangePlanResponse(null);
    onClose();
  };

  const getSelectedPackage = () => {
    if (selectedPackageIndex < 0) return null;
    const package_ = availablePackages[selectedPackageIndex];
    return package_?.package ? package_ : null;
  };

  const calculateTotalDue = () => {
    const selectedPkg = getSelectedPackage();
    if (!selectedPkg?.package) return 0;

    // Calculate the difference between new and current plan
    const priceDifference =
      (selectedPkg.package.price || 0) - (currentPackage.package.price || 0);
    return Math.max(0, priceDifference); // Only show positive amounts
  };

  const getBackgroundSize = () => {
    if (availablePackages.length === 0) return { backgroundSize: "0% 100%" };
    const min = 0;
    const max = availablePackages.length - 1;
    const value = selectedPackageIndex;
    return { backgroundSize: `${((value - min) * 100) / (max - min)}% 100%` };
  };

  const isPackageDisabled = (pkg: IBusinessLinePackage) => {
    // Disable current package and pending updated package
    if (!pkg?.package) return true; // Disable invalid packages
    return (
      pkg.package.id === currentPackage.package?.id ||
      pkg?.pending_updated_package !== null
    );
  };

  const isSelectedPackageDisabled = () => {
    if (selectedPackageIndex < 0) return true;
    const selectedPkg = availablePackages[selectedPackageIndex];
    return isPackageDisabled(selectedPkg);
  };

  const isCurrentPackage = (pkg: IBusinessLinePackage) => {
    return pkg?.package?.id === currentPackage.package?.id;
  };

  const isPendingPackage = (pkg: IBusinessLinePackage) => {
    // Check if this package ID matches the pending updated package ID
    return currentPackage.pending_updated_package?.id === pkg?.package?.id;
  };

  // Summary Dialog Component
  const SummaryDialog = () => {
    if (!changePlanResponse) return null;

    const { business_line_package, type, error } = changePlanResponse;

    // Check if this is a payment failure
    const isPaymentFailure = type === "payment_failed" || error?.status === 402;

    // Determine if this is an immediate change or pending change
    const isImmediate = effectiveFrom === "immediately";

    // For immediate changes, the new package data is in business_line_package.package
    // For pending changes, the new package data is in business_line_package.pending_updated_package
    const newPackage = isImmediate
      ? business_line_package.package
      : business_line_package.pending_updated_package;

    // For immediate changes, we need to get the original package from the dialog
    // For pending changes, the current package is still in business_line_package.package
    const currentPackageData = isImmediate
      ? currentPackage.package
      : business_line_package.package;

    const currentMinutes = currentPackageData.minutes;
    const newMinutes = newPackage?.minutes;
    const currentPrice = currentPackageData.price;
    const newPrice = newPackage?.price;
    const minutesDiff = Math.abs((newMinutes || 0) - currentMinutes);
    const priceDiff = Math.abs((newPrice || 0) - currentPrice);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div
                className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  isPaymentFailure ? "bg-red-100" : "bg-green-100"
                }`}
              >
                {isPaymentFailure ? (
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <FiCheckCircle className="w-5 h-5 text-green-600" />
                )}
              </div>
              <div>
                <h2
                  className={`text-xl font-semibold ${
                    isPaymentFailure ? "text-red-900" : "text-gray-900"
                  }`}
                >
                  {isPaymentFailure
                    ? "Payment Failed"
                    : "Plan Change Successful!"}
                </h2>
                <p
                  className={`text-sm ${
                    isPaymentFailure ? "text-red-600" : "text-gray-600"
                  }`}
                >
                  {isPaymentFailure
                    ? "Your payment method was declined. Please update your card details."
                    : "Your plan change request has been submitted successfully"}
                </p>
              </div>
            </div>
            <button
              onClick={handleCloseSummary}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              aria-label="Close dialog"
            >
              <FiX className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          {/* Summary Content */}
          <div className="p-6 space-y-6">
            {/* Success/Error Message */}
            <div
              className={`border rounded-lg p-4 ${
                isPaymentFailure
                  ? "bg-red-50 border-red-200"
                  : "bg-green-50 border-green-200"
              }`}
            >
              <div className="flex items-center space-x-2">
                {isPaymentFailure ? (
                  <svg
                    className="w-5 h-5 text-red-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                ) : (
                  <FiCheckCircle className="w-5 h-5 text-green-600" />
                )}
                <span
                  className={`font-medium ${
                    isPaymentFailure ? "text-red-800" : "text-green-800"
                  }`}
                >
                  {isPaymentFailure
                    ? error?.message ||
                      "Payment failed. Please check your payment method."
                    : "Plan change request submitted successfully!"}
                </span>
              </div>
            </div>

            {/* Phone Number */}
            <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
              <FiPhone className="w-5 h-5 text-gray-600" />
              <div>
                <span className="text-sm text-gray-600">Phone Number</span>
                <div className="font-medium text-gray-900">
                  {business_line_package.phone_number}
                </div>
              </div>
            </div>

            {/* Package Details */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Current Package */}
              <div className="p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">
                  {isImmediate ? "Previous Package" : "Current Package"}
                </h3>
                <div className="space-y-2">
                  <div className="font-semibold text-gray-900">
                    {currentPackageData.name}
                  </div>
                  <div className="text-2xl font-bold text-gray-900">
                    {currentMinutes} min
                  </div>
                  <div className="text-lg font-medium text-gray-700">
                    ${currentPrice}/month
                  </div>
                </div>
              </div>

              {/* New Package */}
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-blue-600 mb-2">
                  {isImmediate ? "New Active Package" : "New Package"}
                </h3>
                <div className="space-y-2">
                  <div className="font-semibold text-blue-900">
                    {newPackage?.name}
                  </div>
                  <div className="text-2xl font-bold text-blue-900">
                    {newMinutes} min
                  </div>
                  <div className="text-lg font-medium text-blue-700">
                    ${newPrice}/month
                  </div>
                </div>
              </div>
            </div>

            {/* Change Summary */}
            <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 mb-3">
                {type === "upgrade" ? (
                  <FiTrendingUp className="w-5 h-5 text-green-600" />
                ) : (
                  <FiTrendingDown className="w-5 h-5 text-orange-600" />
                )}
                <span
                  className={`font-semibold ${
                    type === "upgrade" ? "text-green-800" : "text-orange-800"
                  }`}
                >
                  {type === "upgrade" ? "Upgrade" : "Downgrade"}
                </span>
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Minutes Change:</span>
                  <span
                    className={`font-medium ${
                      type === "upgrade" ? "text-green-600" : "text-orange-600"
                    }`}
                  >
                    {type === "upgrade" ? "+" : "-"}
                    {minutesDiff} minutes
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Price Change:</span>
                  <span
                    className={`font-medium ${
                      type === "upgrade" ? "text-green-600" : "text-orange-600"
                    }`}
                  >
                    {type === "upgrade" ? "+" : "-"}${priceDiff}/month
                  </span>
                </div>
              </div>
            </div>

            {/* Effective Date */}
            <div className="flex items-center space-x-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <FiCalendar className="w-5 h-5 text-yellow-600" />
              <div>
                <span className="text-sm text-yellow-800 font-medium">
                  Effective Date
                </span>
                <div className="text-yellow-900">
                  {effectiveFrom === "immediately"
                    ? "Immediately"
                    : "Next Billing Cycle"}
                </div>
              </div>
            </div>

            {/* Charges Section */}
            {changePlanResponse.charges &&
              changePlanResponse.charges.length > 0 && (
                <div
                  className={`border rounded-lg p-4 ${
                    isPaymentFailure
                      ? "bg-red-50 border-red-200"
                      : "bg-blue-50 border-blue-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold mb-3 ${
                      isPaymentFailure ? "text-red-900" : "text-blue-900"
                    }`}
                  >
                    {isPaymentFailure ? "Failed Charges" : "Charges Applied"}
                  </h3>
                  <div className="space-y-3">
                    {changePlanResponse.charges.map((charge, index) => (
                      <div
                        key={charge.id}
                        className={`bg-white rounded-lg p-3 border ${
                          isPaymentFailure
                            ? "border-red-100"
                            : "border-blue-100"
                        }`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="font-medium text-gray-900">
                              Charge #{index + 1}
                            </div>
                            <div className="text-sm text-gray-600">
                              {new Date(charge.created).toLocaleDateString(
                                "en-US",
                                {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                }
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-semibold text-gray-900">
                              ${charge.amount.toFixed(2)}{" "}
                              {charge.currency.toUpperCase()}
                            </div>
                            <span
                              className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                charge.status === "succeeded"
                                  ? "bg-green-100 text-green-800"
                                  : charge.status === "failed"
                                  ? "bg-red-100 text-red-800"
                                  : "bg-yellow-100 text-yellow-800"
                              }`}
                            >
                              {charge.status}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            {/* Invoice Section */}
            {changePlanResponse.invoice && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-green-900 mb-3">
                  Invoice Generated
                </h3>
                <div className="bg-white rounded-lg p-4 border border-green-100">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm text-gray-600 mb-1">
                        Invoice Number
                      </div>
                      <div className="font-mono font-medium text-gray-900">
                        {changePlanResponse.invoice.number}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">
                        Total Amount
                      </div>
                      <div className="font-semibold text-gray-900">
                        ${changePlanResponse.invoice.total.toFixed(2)}{" "}
                        {changePlanResponse.invoice.currency.toUpperCase()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Period</div>
                      <div className="text-sm text-gray-900">
                        {new Date(
                          changePlanResponse.invoice.period_start
                        ).toLocaleDateString()}{" "}
                        -{" "}
                        {new Date(
                          changePlanResponse.invoice.period_end
                        ).toLocaleDateString()}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Status</div>
                      <span
                        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          changePlanResponse.invoice.status === "paid"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {changePlanResponse.invoice.status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() =>
                        window.open(
                          changePlanResponse.invoice?.invoice_pdf,
                          "_blank"
                        )
                      }
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                      View Invoice PDF
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Additional Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <FiInfo className="w-5 h-5 text-gray-500 mt-0.5" />
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">What happens next?</p>
                  <ul className="space-y-1">
                    {isImmediate ? (
                      <>
                        <li>• Your plan has been changed immediately</li>
                        <li>• The new plan is now active and in use</li>
                        <li>• Any charges have been processed and invoiced</li>
                        <li>
                          • Your next billing cycle will use the new plan
                          pricing
                        </li>
                      </>
                    ) : (
                      <>
                        <li>
                          • Your current plan remains active until the effective
                          date
                        </li>
                        <li>
                          • The new plan will be activated automatically on the
                          next billing cycle
                        </li>
                        <li>
                          • Any billing adjustments will be reflected in your
                          next invoice
                        </li>
                        <li>
                          • You can cancel this change request by contacting
                          support
                        </li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleCloseSummary}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  isPaymentFailure
                    ? "bg-red-600 text-white hover:bg-red-700"
                    : "bg-[#0cb17b] text-white hover:bg-[#0a9e6a]"
                }`}
              >
                {isPaymentFailure ? "Update Payment Method" : "Got it!"}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  // Show summary dialog if available
  if (showSummary) {
    return <SummaryDialog />;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <FiPackage className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Change plan
              </h2>
              <p className="text-sm text-gray-600">
                Select a new plan for {currentPackage.phone_number} (
                {currentPackage.package?.type || "regular"})
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <FiX className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="flex flex-col lg:flex-row gap-8 p-6">
          {/* Left Side - Package Selection with Slider */}
          <div className="flex-1">
            <h3 className="text-lg font-medium text-gray-900 mb-6 text-center">
              Available Plans
            </h3>

            {/* Loading State */}
            {isLoadingPackages && (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  Loading available packages...
                </div>
              </div>
            )}

            {/* Selected Package Display Card - Similar to select_subscription */}
            {!isLoadingPackages &&
              availablePackages.length > 0 &&
              selectedPackageIndex >= 0 &&
              (() => {
                const selectedPackage = availablePackages[selectedPackageIndex];
                if (!selectedPackage?.package) return null;

                return (
                  <div className="flex flex-col gap-6 bg-gradient-to-br from-[#00b67a] to-[#1dc7ff] p-6 rounded-2xl text-white text-center mb-8 w-full mx-auto">
                    <div className="text-xl font-bold text-start">
                      ${(selectedPackage.package.price || 0).toFixed(2)}/month
                    </div>
                    <div className="text-6xl font-bold text-center">
                      {selectedPackage.package.minutes || 0} min
                    </div>
                    <div className="text-sm text-end">
                      $
                      {(
                        selectedPackage.package.fee_per_overage_minute || 0
                      ).toFixed(2)}{" "}
                      per add. min
                    </div>
                    <div className="text-lg font-semibold text-center">
                      {selectedPackage.package.name}
                    </div>
                  </div>
                );
              })()}

            {/* Package Slider */}
            {!isLoadingPackages && availablePackages.length > 0 && (
              <div className="relative w-full mb-8">
                <style>
                  {`
                  input[type="range"] {
                    -webkit-appearance: none;
                    background-color: #edeff7;
                    border-radius: 8px;
                    height: 4px;
                    width: 100%;
                    background-image: linear-gradient(#0cb17b, #0cb17b);
                    background-repeat: no-repeat;
                  }

                  input[type="range"]::-webkit-slider-thumb {
                    -webkit-appearance: none;
                    height: 20px;
                    width: 20px;
                    border-radius: 50%;
                    background: #0cb17b;
                    cursor: pointer;
                    box-shadow: 0 0 2px 0 rgb(0 0 0 / 10%);
                  }

                  input[type="range"]::-webkit-slider-runnable-track {
                    -webkit-appearance: none;
                    box-shadow: none;
                    border: none;
                    background: transparent;
                  }

                  .milestone {
                    width: 2px;
                    height: 10px;
                    background: #0cb17b;
                    position: absolute;
                    top: -3px;
                  }
                `}
                </style>
                <div className="relative">
                  <input
                    type="range"
                    min={0}
                    max={availablePackages.length - 1}
                    value={selectedPackageIndex}
                    onChange={(e) => {
                      const index = Number(e.target.value);
                      const pkg = availablePackages[index];
                      console.log("Slider changed to index:", index);

                      // Only allow selection of non-disabled packages
                      if (!isPackageDisabled(pkg)) {
                        setSelectedPackageIndex(index);
                      }
                    }}
                    step={1}
                    style={getBackgroundSize()}
                    className="w-full"
                    aria-label="Select package"
                  />
                  {availablePackages.map(
                    (pkg: IBusinessLinePackage, index: number) => {
                      if (!pkg) return null;
                      return (
                        <div
                          key={pkg.id || index}
                          className={`milestone ${
                            isCurrentPackage(pkg)
                              ? "bg-blue-500"
                              : isPendingPackage(pkg)
                              ? "bg-orange-500"
                              : "bg-[#0cb17b]"
                          }`}
                          style={{
                            left: `${
                              (index / (availablePackages.length - 1)) * 100
                            }%`,
                          }}
                        />
                      );
                    }
                  )}
                </div>
                <div className="flex justify-between mt-4">
                  {availablePackages.map(
                    (pkg: IBusinessLinePackage, index: number) => {
                      if (!pkg) return null;
                      return (
                        <div
                          key={pkg.id || index}
                          className="relative"
                          style={{
                            position: "absolute",
                            left: `${
                              (index / (availablePackages.length - 1)) * 100
                            }%`,
                            transform: "translateX(-50%)",
                          }}
                        >
                          {/* Package Minutes Label */}
                          <div
                            className={`text-xs ${
                              selectedPackageIndex === index
                                ? "text-[#0cb17b] font-bold"
                                : isPackageDisabled(pkg)
                                ? "text-gray-400"
                                : "text-[#aaacb3]"
                            }`}
                          >
                            {pkg?.package?.minutes
                              ? `${pkg.package.minutes}min`
                              : "N/A"}
                          </div>

                          {/* Status Badge - Moved below the numbers */}
                          {(isCurrentPackage(pkg) || isPendingPackage(pkg)) && (
                            <div
                              className={`absolute top-6 left-1/2 transform -translate-x-1/2 text-xs px-2 py-1 rounded-full whitespace-nowrap ${
                                isCurrentPackage(pkg)
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-orange-100 text-orange-800"
                              }`}
                            >
                              {isCurrentPackage(pkg) ? "Current" : "Pending"}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              </div>
            )}

            {/* No Packages State */}
            {!isLoadingPackages && availablePackages.length === 0 && (
              <div className="text-center py-8">
                <div className="text-gray-500">
                  No packages available for {phoneType} phone numbers
                </div>
              </div>
            )}
          </div>

          {/* Right Side - Plan Effective and Summary */}
          <div className="w-full lg:w-80 p-6 bg-gray-50 rounded-xl">
            {/* Pending Updated Package Information */}
            {currentPackage.pending_updated_package && (
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-3">
                  Pending Plan Update
                </h3>
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-center space-x-2 mb-2">
                    <FiClock className="text-blue-600 text-lg" />
                    <span className="text-blue-800 font-semibold text-sm">
                      Scheduled Update
                    </span>
                    {/* Upgrade/Downgrade Indicator */}
                    {currentPackage.pending_updated_package.minutes >
                    currentPackage.package.minutes ? (
                      <FiTrendingUp
                        className="text-green-600 text-lg"
                        title="Upgrade"
                      />
                    ) : currentPackage.pending_updated_package.minutes <
                      currentPackage.package.minutes ? (
                      <FiTrendingDown
                        className="text-red-600 text-lg"
                        title="Downgrade"
                      />
                    ) : null}
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-blue-700 text-sm font-medium">
                        {currentPackage.pending_updated_package.name}
                      </span>
                      <span className="text-blue-800 font-bold text-sm">
                        $
                        {currentPackage.pending_updated_package.price.toFixed(
                          2
                        )}
                      </span>
                    </div>

                    <div className="flex justify-between items-center text-xs text-blue-600">
                      <span>
                        {currentPackage.pending_updated_package.minutes} minutes
                        {currentPackage.pending_updated_package.minutes >
                        currentPackage.package.minutes ? (
                          <span className="text-green-600 ml-1">
                            (+
                            {currentPackage.pending_updated_package.minutes -
                              currentPackage.package.minutes}
                            )
                          </span>
                        ) : currentPackage.pending_updated_package.minutes <
                          currentPackage.package.minutes ? (
                          <span className="text-red-600 ml-1">
                            (-
                            {currentPackage.package.minutes -
                              currentPackage.pending_updated_package.minutes}
                            )
                          </span>
                        ) : null}
                      </span>
                      <span className="flex items-center space-x-1">
                        <FiInfo className="text-blue-500" />
                        <span>Effective next billing cycle</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Plan Effective From */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Plan Effective From
              </h3>
              <div className="space-y-3">
                <label className="flex items-center space-x-3">
                  <input
                    type="radio"
                    name="effectiveFrom"
                    value="immediately"
                    checked={effectiveFrom === "immediately"}
                    onChange={(e) =>
                      setEffectiveFrom(
                        e.target.value as "immediately" | "next_billing_cycle"
                      )
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Immediately</span>
                </label>
                <label className="flex items-center space-x-3">
                  <input
                    type="radio"
                    name="effectiveFrom"
                    value="next_billing_cycle"
                    checked={effectiveFrom === "next_billing_cycle"}
                    onChange={(e) =>
                      setEffectiveFrom(
                        e.target.value as "immediately" | "next_billing_cycle"
                      )
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Next Billing Cycle</span>
                </label>
              </div>
            </div>

            {/* Upgrade Summary */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-3">
                Upgrade Summary
              </h3>
              <div className="bg-white rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700">Total due</span>
                  <span className="text-2xl font-bold text-gray-900">
                    ${calculateTotalDue().toFixed(2)}
                  </span>
                </div>
              </div>
            </div>

            {/* Warning Message for Disabled Packages */}
            {selectedPackageIndex >= 0 && isSelectedPackageDisabled() && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-3">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full"></div>
                  <span className="text-sm text-yellow-800">
                    {availablePackages[selectedPackageIndex] &&
                    isCurrentPackage(availablePackages[selectedPackageIndex])
                      ? "This is your current package. Please select a different plan."
                      : "This package has a pending update. Please select a different plan."}
                  </span>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={handleConfirm}
                disabled={
                  selectedPackageIndex === -1 ||
                  isLoading ||
                  isSelectedPackageDisabled()
                }
                className={`w-full py-3 px-6 rounded-lg font-medium transition-colors ${
                  selectedPackageIndex !== -1 &&
                  !isLoading &&
                  !isSelectedPackageDisabled()
                    ? "bg-[#0cb17b] text-white hover:bg-[#0a9e6a]"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                {isLoading
                  ? "Processing..."
                  : effectiveFrom === "immediately"
                  ? "Pay & Confirm"
                  : "Confirm"}
              </button>
              <button
                onClick={handleClose}
                className="w-full py-3 px-6 border-2 border-gray-200 text-gray-900 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 pb-8 border-t border-gray-200 bg-gray-50">
          <p className="text-sm text-gray-600 text-center">
            To unsubscribe, please contact our support at (949) 943-6318 or
            jay@cfbtel.com
          </p>
        </div>
      </div>
    </div>
  );
};

export default ChangePlanDialog;
